import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  TablePagination,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  Event as EventIcon,
  CheckCircle,
  EventAvailable,
  Add as AddIcon,
  AddCircle as AddCircleIcon,
  CalendarViewMonth as CalendarViewMonthIcon,
  ViewList as ViewListIcon,
} from "@mui/icons-material";
import { addDays, startOfMonth, endOfMonth, isWithinInterval } from "date-fns";
import MobileDataCard, {
  MobileDataList,
} from "../../components/common/MobileDataCard";
import BotonRecibo from "../../components/BotonRecibo";
import ColaboradorEventoModal from "../../components/colaborador/ColaboradorEventoModal";
import EventForm from "../../components/admin/EventForm";
import CalendarDisponibilidad from "../../components/common/CalendarDisponibilidad";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import {
  formatDateShort,
  parseFechaLocal,
  getCurrentMonth,
  getCurrentYear,
} from "../../utils/dateUtils";

const ESTADO_COLORS = {
  Presupuesto: "#9E9E9E",
  Pendiente: "#FFC107",
  Confirmado: "#2196F3",
  "En curso": "#9C27B0",
  Completado: "#4CAF50",
  Cancelado: "#F44336",
};

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// ─── Tarjeta de KPI circular ────────────────────────────────────────────────
const KpiCard = ({ title, value, icon, color }) => (
  <Box
    sx={{
      width: { xs: 112, sm: 145 },
      height: { xs: 112, sm: 145 },
      borderRadius: "50%",
      background: `radial-gradient(circle at 35% 35%, ${color}40, ${color}15)`,
      border: `2px solid ${color}50`,
      boxShadow: `0 0 22px ${color}30`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: { xs: 0.3, sm: 0.5 },
      mx: "auto",
      transition: "transform .2s, box-shadow .2s",
      "&:hover": { transform: "scale(1.06)", boxShadow: `0 0 32px ${color}50` },
    }}
  >
    <Box sx={{ color, lineHeight: 1, "& .MuiSvgIcon-root": { fontSize: { xs: 24, sm: 28 } } }}>
      {icon}
    </Box>
    <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.15rem", sm: "1.4rem" }, color: "#fff", lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: { xs: "0.52rem", sm: "0.62rem" }, color: "rgba(255,255,255,0.72)", fontWeight: 600, textAlign: "center", lineHeight: 1.2, px: 1 }}>
      {title}
    </Typography>
  </Box>
);

// ─── Componente principal ────────────────────────────────────────────────────
const ColaboradorMisEventos = () => {
  const { user } = useAuth();

  const [eventos, setEventos] = useState([]);
  const [filteredEventos, setFilteredEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Formulario de creación
  const [formMode, setFormMode] = useState(false); // false = lista, true = crear

  // Modal detalle evento (colaborador)
  const [detalleEvento, setDetalleEvento] = useState(null);
  const [detalleOpen, setDetalleOpen] = useState(false);
  // Vista: lista o calendario de disponibilidad
  const [vistaCalendario, setVistaCalendario] = useState(false);
  // Filtros de fecha
  const [dateFilterTab, setDateFilterTab] = useState(3); // Todos por defecto
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth() - 1);
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());

  // ── Stats derivadas ──────────────────────────────────────────────────────
  const totalEventos = eventos.length;
  const completados = eventos.filter((e) => e.estado === "Completado").length;
  const proximos = eventos.filter((e) => {
    const f = parseFechaLocal(e.fecha_evento || e.fecha_inicio);
    if (!f) return false;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return f >= hoy;
  }).length;
  const generados = eventos.filter(
    (e) => String(e.colaborador_creador_id) === String(user?.colaborador?.id)
  ).length;

  // ── Carga de datos ───────────────────────────────────────────────────────
  const loadEventos = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError("");

      // Endpoint seguro: devuelve solo eventos generados + asignados del colaborador
      const res = await api.get("/colaborador/eventos");
      const lista = res.data?.eventos || [];

      setEventos(Array.isArray(lista) ? lista : []);
    } catch (err) {
      if (err.response?.status !== 401) {
        setError("Error al cargar tus eventos");
      }
      setEventos([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadEventos();
  }, [loadEventos]);

  // ── Filtrado ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let filtered = [...eventos];

    if (dateFilterTab === 0) {
      const end = addDays(today, 8);
      filtered = filtered.filter((e) => {
        const f = parseFechaLocal(e.fecha_evento || e.fecha_inicio);
        return f && f >= today && f <= end;
      });
    } else if (dateFilterTab === 1) {
      const end = addDays(today, 15);
      filtered = filtered.filter((e) => {
        const f = parseFechaLocal(e.fecha_evento || e.fecha_inicio);
        return f && f >= today && f <= end;
      });
    } else if (dateFilterTab === 2) {
      const start = startOfMonth(new Date(selectedYear, selectedMonth, 1));
      const end = endOfMonth(new Date(selectedYear, selectedMonth, 1));
      filtered = filtered.filter((e) => {
        const f = parseFechaLocal(e.fecha_evento || e.fecha_inicio);
        return f && isWithinInterval(f, { start, end });
      });
    }
    // Tab 3 = Todos

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.titulo?.toLowerCase().includes(term) ||
          e.cliente_nombre?.toLowerCase().includes(term) ||
          e.ubicacion?.toLowerCase().includes(term) ||
          e.estado?.toLowerCase().includes(term),
      );
    }

    filtered.sort((a, b) => {
      const fa = parseFechaLocal(a.fecha_evento || a.fecha_inicio) || 0;
      const fb = parseFechaLocal(b.fecha_evento || b.fecha_inicio) || 0;
      return fa - fb;
    });

    setFilteredEventos(filtered);
    setPage(0);
  }, [eventos, searchTerm, dateFilterTab, selectedMonth, selectedYear]);

  const displayed = filteredEventos.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleFormSuccess = (nuevoEvento) => {
    setFormMode(false);
    loadEventos(); // Recargar para reflejar el nuevo evento
  };

  const handleFormCancel = () => {
    setFormMode(false);
  };

  // ── Render formulario ────────────────────────────────────────────────────
  if (formMode) {
    // Preparar el colaborador bloqueado: solo el usuario firmado
    const lockedColaboradores =
      user?.colaborador
        ? [{ id: user.colaborador.id, nombre_completo: user.colaborador.nombre_completo }]
        : [];

    return (
      <Box>
        <EventForm
          eventoId={null}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
          lockedColaboradores={lockedColaboradores}
        />
      </Box>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Encabezado */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.25rem", sm: "2.125rem" },
          }}
        >
          Mis Eventos
        </Typography>

        {/* Toggle vista + Botón nuevo evento */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Toggle lista / disponibilidad */}
          <Box sx={{ display: "flex", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "10px", overflow: "hidden" }}>
            <Tooltip title="Vista lista">
              <IconButton
                size="small"
                onClick={() => setVistaCalendario(false)}
                sx={{
                  borderRadius: 0, px: 1.2, py: 0.8,
                  bgcolor: !vistaCalendario ? "rgba(99,102,241,0.25)" : "transparent",
                  color: !vistaCalendario ? "#a5b4fc" : "text.secondary",
                  "&:hover": { bgcolor: "rgba(99,102,241,0.15)" },
                }}
              >
                <ViewListIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Disponibilidad de fechas">
              <IconButton
                size="small"
                onClick={() => setVistaCalendario(true)}
                sx={{
                  borderRadius: 0, px: 1.2, py: 0.8,
                  bgcolor: vistaCalendario ? "rgba(99,102,241,0.25)" : "transparent",
                  color: vistaCalendario ? "#a5b4fc" : "text.secondary",
                  "&:hover": { bgcolor: "rgba(99,102,241,0.15)" },
                }}
              >
                <CalendarViewMonthIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Botón nuevo evento (solo en vista lista) */}
          {!vistaCalendario && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setFormMode(true)}
                sx={{
                  display: { xs: "none", sm: "flex" },
                  background: "linear-gradient(45deg, #00C9FF 30%, #92FE9D 90%)",
                  color: "#000", fontWeight: 700,
                  boxShadow: "0 3px 8px rgba(0,201,255,0.4)",
                  "&:hover": { background: "linear-gradient(45deg, #00a8d6 30%, #6ede79 90%)" },
                }}
              >
                Nuevo Evento
              </Button>
              <IconButton
                onClick={() => setFormMode(true)}
                sx={{
                  display: { xs: "flex", sm: "none" },
                  width: 44, height: 44,
                  background: "linear-gradient(45deg, #00C9FF 30%, #92FE9D 90%)",
                  color: "#000",
                  boxShadow: "0 3px 8px rgba(0,201,255,0.4)",
                  "&:hover": { background: "linear-gradient(45deg, #00a8d6 30%, #6ede79 90%)" },
                }}
              >
                <AddIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {/* KPI Cards — 2 filas de 2 */}
      <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center" sx={{ mb: { xs: 2, sm: 3 } }}>
        {[
          { title: "Total Asignados",   value: totalEventos, icon: <EventIcon />,      color: "#00C9FF" },
          { title: "Completados",        value: completados,  icon: <CheckCircle />,    color: "#4CAF50" },
          { title: "Próximos",           value: proximos,     icon: <EventAvailable />, color: "#FFC107" },
          { title: "Eventos Generados",  value: generados,    icon: <AddCircleIcon />,  color: "#C77DFF" },
        ].map((kpi) => (
          <Grid item xs={6} sm="auto" key={kpi.title} sx={{ display: "flex", justifyContent: "center" }}>
            <KpiCard {...kpi} />
          </Grid>
        ))}
      </Grid>

      {/* ── VISTA CALENDARIO DE DISPONIBILIDAD ─────────────────────────────── */}
      {vistaCalendario && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <CalendarViewMonthIcon sx={{ color: "#6366f1", fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.15rem" } }}>
              Disponibilidad de Fechas
            </Typography>
            <Chip label="Solo consulta" size="small"
              sx={{ ml: "auto", height: 20, fontSize: "0.6rem", bgcolor: "rgba(148,163,184,0.12)", color: "#94a3b8" }} />
          </Box>
          <CalendarDisponibilidad userRole="colaborador" />
        </Paper>
      )}

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Ocultar lista cuando está el calendario activo */}
      {vistaCalendario ? null : (
        <>

      {/* Buscador */}
      <Paper sx={{ mb: 2, p: { xs: 1.5, sm: 2 } }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por nombre, cliente, ubicación o estado..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "text.disabled", fontSize: 20 }} />,
          }}
        />
      </Paper>

      {/* Filtros de Fecha */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={dateFilterTab}
          onChange={(_, v) => {
            setDateFilterTab(v);
            setPage(0);
          }}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ "& .MuiTab-root": { fontSize: { xs: "0.72rem", sm: "0.875rem" }, minWidth: { xs: 80, sm: 120 } } }}
        >
          <Tab label="8 días" />
          <Tab label="15 días" />
          <Tab label="Por Mes" />
          <Tab label="Todos" />
        </Tabs>

        {dateFilterTab === 2 && (
          <Box sx={{ p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Mes</InputLabel>
              <Select
                value={selectedMonth}
                label="Mes"
                onChange={(e) => { setSelectedMonth(e.target.value); setPage(0); }}
              >
                {MESES.map((m, i) => (
                  <MenuItem key={i} value={i}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Año</InputLabel>
              <Select
                value={selectedYear}
                label="Año"
                onChange={(e) => { setSelectedYear(e.target.value); setPage(0); }}
              >
                {[2024, 2025, 2026, 2027, 2028].map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
              {filteredEventos.length} evento(s) en {MESES[selectedMonth]} {selectedYear}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Lista de eventos */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <MobileDataList
            loading={false}
            emptyMessage="No tienes eventos asignados en este período"
          >
            {displayed.map((evento) => (
              <MobileDataCard
                key={evento.id}
                title={evento.titulo || "Sin nombre"}
                subtitle={
                  evento.cliente_nombre ||
                  evento.cliente?.nombre_completo ||
                  "Sin cliente"
                }
                avatarColor={ESTADO_COLORS[evento.estado] || "#1976D2"}
                status={{
                  label: evento.estado || "Pendiente",
                  color: ESTADO_COLORS[evento.estado] || "#757575",
                }}
                primaryInfo={[
                  {
                    icon: <CalendarIcon fontSize="small" />,
                    label: "Fecha",
                    value:
                      evento.fecha_evento || evento.fecha_inicio
                        ? formatDateShort(evento.fecha_evento || evento.fecha_inicio)
                        : "-",
                  },
                  {
                    icon: <TimeIcon fontSize="small" />,
                    label: "Hora",
                    value: evento.hora_inicio || "-",
                  },
                ]}
                expandedInfo={[
                  {
                    icon: <LocationIcon fontSize="small" />,
                    label: "Ubicación",
                    value: evento.ubicacion || evento.direccion || "-",
                  },
                  {
                    icon: <TimeIcon fontSize="small" />,
                    label: "Duración",
                    value: evento.duracion_horas
                      ? `${evento.duracion_horas} hrs`
                      : "-",
                  },
                  {
                    icon: <MoneyIcon fontSize="small" />,
                    label: "Costo",
                    value: evento.costo_servicio
                      ? `$${Number(evento.costo_servicio).toLocaleString("es-MX")}`
                      : "-",
                  },
                  {
                    icon: <PersonIcon fontSize="small" />,
                    label: "Rol",
                    value: evento.rol_colaborador || evento.especialidad || "Colaborador",
                  },
                ]}
                // Sin acciones de editar/eliminar para colaborador
                actions={[]}
                customActions={[
                  ...(evento.estado?.trim() === "En curso"
                    ? [
                        <Button
                          key="datos"
                          size="small"
                          variant="contained"
                          color="info"
                          onClick={() => { setDetalleEvento(evento); setDetalleOpen(true); }}
                        >
                          Datos del evento
                        </Button>,
                      ]
                    : []),
                  <BotonRecibo
                    key="recibo"
                    evento={evento}
                    size="small"
                    label="Descargar recibo"
                    disabled={evento.estado !== "Confirmado"}
                    disabledTooltip="Solo disponible cuando el evento está Confirmado"
                  />,
                ]}
              />
            ))}
          </MobileDataList>

          {filteredEventos.length > rowsPerPage && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredEventos.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count}`
              }
              sx={{
                mt: 1,
                "& .MuiTablePagination-toolbar": { flexWrap: "wrap", justifyContent: "center" },
                "& .MuiTablePagination-selectLabel": { display: { xs: "none", sm: "block" } },
              }}
            />
          )}
        </Box>
      )}

        </> /* fin fragmento lista (vistaCalendario ? null : <>...</>) */
      )}

      {/* Modal detalle del evento para colaboradores (En curso) */}
      <ColaboradorEventoModal
        open={detalleOpen}
        onClose={() => { setDetalleOpen(false); setDetalleEvento(null); }}
        evento={detalleEvento}
      />
    </Box>
  );
};

export default ColaboradorMisEventos;
